import { useCallback, useEffect, useState } from "react";
import {
  extractHrSamplesFromPayload,
  extractHrClearKeysFromPayload,
  HR_HISTORY_WINDOW_MS,
  ingestPayloadIntoHistory,
  isLiveHrSample,
  playerHistoryKey,
  samplesForPlayer,
  trimHistoryMap,
} from "../utils/liveHrHistoryBuffer";

/**
 * Rolling 60s HR history keyed by player_id / user_id from live socket events.
 */
export function useLiveHrHistory({
  socket,
  tsId,
  currentTeId,
  enabled = true,
}) {
  const [historyMap, setHistoryMap] = useState({});
  const [liveBpmMap, setLiveBpmMap] = useState({});

  useEffect(() => {
    setHistoryMap({});
    setLiveBpmMap({});
  }, [tsId]);

  useEffect(() => {
    if (!enabled || !socket || !tsId) return undefined;

    const onPayload = (payload) => {
      if (!payload) return;
      if (payload.ts_id && String(payload.ts_id) !== String(tsId)) return;
      setHistoryMap((prev) => ingestPayloadIntoHistory(prev, payload));
      const samples = extractHrSamplesFromPayload(payload);
      const clearKeys = extractHrClearKeysFromPayload(payload);
      if (!samples.length && !clearKeys.length) return;
      setLiveBpmMap((prev) => {
        let next = prev;
        let changed = false;
        if (clearKeys.length) {
          next = { ...next };
          for (const key of clearKeys) {
            if (next[key] != null) {
              delete next[key];
              changed = true;
            }
          }
        }
        if (samples.length) {
          if (!changed) next = { ...next };
          for (const sample of samples) {
            next[sample.key] = { bpm: sample.bpm, t: sample.t };
          }
          changed = true;
        }
        return changed ? next : prev;
      });
    };

    socket.on("hr-update", onPayload);
    socket.on("player_heart_rate", onPayload);
    socket.on("playerHeartRate", onPayload);

    return () => {
      socket.off("hr-update", onPayload);
      socket.off("player_heart_rate", onPayload);
      socket.off("playerHeartRate", onPayload);
    };
  }, [socket, tsId, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => {
      setHistoryMap((prev) => trimHistoryMap(prev, HR_HISTORY_WINDOW_MS));
    }, 5000);
    return () => window.clearInterval(id);
  }, [enabled]);

  const getSamples = useCallback(
    (playerId, userId) => samplesForPlayer(historyMap, playerId, userId),
    [historyMap]
  );

  const getLiveBpm = useCallback(
    (playerId, userId, nowMs = Date.now()) => {
      const key = playerHistoryKey(playerId, userId);
      if (!key) return null;
      const entry = liveBpmMap[key];
      if (!entry) return null;
      if (!isLiveHrSample(entry.t, nowMs)) return null;
      return entry.bpm;
    },
    [liveBpmMap]
  );

  const requestHrSnapshot = useCallback(() => {
    if (!socket || !tsId || !currentTeId) return;
    socket.emit("requestHrSnapshot", { ts_id: tsId, te_id: currentTeId });
  }, [socket, tsId, currentTeId]);

  return { getSamples, getLiveBpm, requestHrSnapshot, historyMap, liveBpmMap };
}
